import React, { useState, useRef } from "react";
import { Box, Tooltip } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { MRT_ColumnDef } from "material-react-table";
import dayjs from "dayjs";
import { ExtendedScorecardDataItem } from "api";
import { formatAuditDate } from "utils";
import { RowActions } from "../components/tableActions/rowActionsComponents/rowActions";
import { EditCellValueModal } from "../components/tableActions/cellActionsComponents/modals/editCellValueModal/EditCellValueModal";
import { ActionIcons } from "../components/tableActions/cellActionsComponents/ActionIcons";

export type PeriodType = "Monthly" | "Quarterly" | "Yearly";

export interface GetScorecardColumnsParams {
  periodType: PeriodType;
  dateRanges: { startDate: string; endDate: string; fiscalYear?: number }[];
  isAdmin: boolean;
  selectedUnit: 'INPUT_UNIT' | 'DEFAULT_UNIT';
  isOnEditMode?: boolean;
  isArchivedTable?: boolean;
  isDataProvider?: boolean;
  currentTimeframe?: string;
  onSuccess?: () => void;
  userRoles: string[];
}

const createCommonCellProps = (isArchivedTable: boolean, isCostRow: boolean) => ({
  sx: {
    backgroundColor: isCostRow
      ? "#e8f5e1"
      : (isArchivedTable ? "#E5EAF3" : "transparent"),
    borderRight: "1px solid #C0D0D4",
    borderBottom: "1px solid #C0D0D4",
  },
});

const createCommonHeaderProps = (isArchivedTable: boolean) => ({
  sx: {
    backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
    fontWeight: 600,
    color: "#054254",
    paddingRight: "16px",
    borderRight: "1px solid #C0D0D4",
    borderBottom: "1px solid #C0D0D4",
    "& .MuiTableSortLabel-root": {
      display: "none",
    },
  },
});


// Helper function to create dynamic column cell
const createDynamicColumnCell = (
  timeframeGroup: string,
  isArchivedTable: boolean,
  currentTimeframe: string,
  onSuccess: () => void,
  userRoles: string[],
  disableAllCells: boolean
) => function DynamicColumnCell({ row }: { row: any }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  const value = row.original.values.find((v: any) => v.timeframeGroup === timeframeGroup);
  if (!value) return null;

  const isDisabled = value.inputTimeframe &&
    value.inputTimeframe.toLowerCase() !== currentTimeframe.toLowerCase();

  const handleCellClick = () => {
    if (!isArchivedTable && !isDisabled) {
      setIsEditModalOpen(true);
    }
  };

  const getTooltipMessage = () => {
    if (isDisabled && value.inputTimeframe) {
      return <div style={{ fontWeight: 600, color: '#3D4757', width: '270px' }}>Data was entered in the {value.inputTimeframe} time period. To update, please return to that period.</div>;
    }
    return "";
  };

  const getAuditTooltipContent = () => {
    if (!value.lastModifiedBy && !value.updateDate) {
      return null;
    }

    return (
      <div>
        <div style={{ fontWeight: 700, color: '#3D4757' }}>
          Added by: {value.lastModifiedBy || 'Unknown'}
        </div>
        <div style={{ fontWeight: 600, color: '#6B7280' }}>
          Last modified: {formatAuditDate(value.updateDate)}
        </div>
      </div>
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCellClick();
    }
  };

  const cellValueContent = (
    <div
      style={{
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: !isArchivedTable && !isDisabled ? "pointer" : "default",
        padding: "4px 0",
        flex: 1,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center'
      }}
      onClick={!isArchivedTable && !isDisabled ? handleCellClick : undefined}
      onKeyDown={!isArchivedTable && !isDisabled ? handleKeyDown : undefined}
      role={!isArchivedTable && !isDisabled ? "button" : "text"}
      tabIndex={!isArchivedTable && !isDisabled ? 0 : undefined}
    >
      {value.value} {value.unit}
    </div>
  );

  const auditTooltipContent = getAuditTooltipContent();

  if (disableAllCells) {
    return <div style={{
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      lineHeight: "1.4",
      margin: 0,
    }}>
      {value.value} {value.unit}
    </div>
  }

  return (
    <>
      <Box
        ref={cellRef}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          "&:hover": !isArchivedTable && !isDisabled ? {
            backgroundColor: "rgba(5, 66, 84, 0.05)"
          } : {}
        }}
      >
        {isDisabled || auditTooltipContent ? (
          <Tooltip
            title={isDisabled ? getTooltipMessage() : auditTooltipContent}
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  width: 'fit-content',
                  borderRadius: '5px',
                  opacity: 0.88,
                  display: 'flex',
                  padding: '8px 12px',
                  background: '#F3F4F6',
                  boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                  fontSize: '12px',
                  maxWidth: 'none',
                }
              }
            }}
          >
            {cellValueContent}
          </Tooltip>
        ) : (
          cellValueContent
        )}
        <ActionIcons
          isArchivedTable={isArchivedTable}
          dataId={value.scorecardDataId}
          initialNotes={value.notes}
          cellData={value}
          userRoles={userRoles}
          isDisabled={isDisabled || !value.inputTimeframe}
          isEditModalOpen={isEditModalOpen}
        />
      </Box>
      <EditCellValueModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
        }}
        cellData={value}
        rowId={row.original.rowId}
        availableUnits={row.original.availableUnits}
        currentTimeframe={currentTimeframe}
        onSuccess={onSuccess}
        anchorEl={cellRef.current}
        isCostRow={row.original.isCostRow}
        defaultUnit={row.original.defaultUnit}
        defaultCurrency={row.original.defaultCurrency}
      />
    </>
  );
};

// Helper function to create dynamic column
const createDynamicColumn = (
  id: string,
  header: string,
  isArchivedTable: boolean,
  currentTimeframe: string,
  onSuccess: () => void,
  userRoles: string[],
  disableAllCells: boolean
): MRT_ColumnDef<ExtendedScorecardDataItem> => ({
  id,
  header,
  size: 150,
  minSize: 100,
  enableColumnFilter: true,
  enableColumnActions: true,
  enableSorting: true,
  accessorFn: (row) => {
    const value = row.values?.find((v: any) => v.timeframeGroup === id);
    return value?.value ?? '';
  },
  filterFn: (row, columnId, filterValue) => {
    const cellValue = row.getValue(columnId);
    const filterStr = String(filterValue).toLowerCase().trim();

    // If filtering by "0", match null, undefined, empty string, and 0 values
    if (filterStr === '0') {
      return cellValue === null ||
        cellValue === undefined ||
        cellValue === '' ||
        cellValue === 0 ||
        String(cellValue) === '0';
    }

    // Default: case-insensitive contains match
    return String(cellValue).toLowerCase().includes(filterStr);
  },
  Cell: createDynamicColumnCell(id, isArchivedTable, currentTimeframe, onSuccess, userRoles, disableAllCells),
  muiTableHeadCellProps: createCommonHeaderProps(isArchivedTable),
  muiTableBodyCellProps: ({ row }) => createCommonCellProps(
    isArchivedTable,
    (row.original as ExtendedScorecardDataItem).isCostRow ?? false
  ),
});

/**
 * Generates columns for the scorecard table based on the period type and multiple date ranges
 * @param periodType - The type of period (Monthly, Quarterly, Yearly)
 * @param dateRanges - Array of date ranges for multiple fiscal years
 * @param userRole - The user role object containing permission flags
 * @param selectedUnit - The selected unit (INPUT_UNIT or DEFAULT_UNIT)
 * @param isOnEditMode - Whether the table is in edit mode
 * @param isArchivedTable - Whether this is an archived data table
 * @param isDataProvider - Whether the user is a data provider
 * @returns Array of column definitions for Material React Table
 */
export const getScorecardColumns = ({
  periodType,
  dateRanges,
  isAdmin,
  selectedUnit,
  isOnEditMode = false,
  isArchivedTable = false,
  currentTimeframe = '',
  onSuccess = () => { },
  userRoles,
}: GetScorecardColumnsParams): MRT_ColumnDef<ExtendedScorecardDataItem>[] => {

  const shouldShowActions = isAdmin;
  const disableAllCells = selectedUnit === 'DEFAULT_UNIT';
  const shouldDisableActions = disableAllCells || !isOnEditMode;


  const labelsColumn: MRT_ColumnDef<ExtendedScorecardDataItem> = {
    id: "labels",
    accessorFn: (row) => (row as ExtendedScorecardDataItem).isCostRow ?? false,
    header: "Labels",
    size: 80,
    minSize: 80,
    enableColumnActions: false,
    enableSorting: false,
    enableResizing: false,
    enableColumnFilter: false,
    enablePinning: true,
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
        fontWeight: 600,
        color: "#054254",
        paddingRight: "16px",
        paddingLeft: "16px",
        borderRight: "1px solid #C0D0D4",
        borderBottom: "1px solid #C0D0D4",
        "& .MuiCollapse-root": {
          display: "none",
        },
      },
    },
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
          ? "#e8f5e1"
          : (isArchivedTable ? "#E5EAF3" : "#F8FAFC"),
        borderRight: "1px solid #C0D0D4",
        borderBottom: "1px solid #C0D0D4",
        padding: "4px 16px",
      },
    }),
    Cell: ({ row }) => {
      const isCostRow = (row.original as ExtendedScorecardDataItem).isCostRow ?? false;
      if (!isCostRow) {
        return null;
      }
      return (
        <Tooltip title="Cost Entry" placement="top">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
            data-testid={`is-cost-row-${row.original.rowId}`}
          >
            <AttachMoneyIcon sx={{ fontSize: 20, color: "#054254" }} aria-label="Cost Entry" />
          </Box>
        </Tooltip>
      );
    },
  };

  const actionsColumn: MRT_ColumnDef<ExtendedScorecardDataItem> | null = shouldShowActions ? {
    accessorKey: "actions",
    header: "Actions",
    size: 125,
    minSize: 125,
    enableColumnActions: false,
    enableSorting: false,
    enableResizing: false,
    enablePinning: true,
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
        fontWeight: 600,
        color: "#054254",
        paddingRight: "16px",
        borderRight: "1px solid #C0D0D4",
        borderBottom: "1px solid #C0D0D4",
        "& .MuiCollapse-root": {
          display: "none",
        },
      },
    },
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
          ? "#e8f5e1"
          : (isArchivedTable ? "#E5EAF3" : "#F8FAFC"),
        borderRight: "1px solid #C0D0D4",
        borderBottom: "1px solid #C0D0D4",
      },
    }),
    Cell: ({ row }) => {
      // Don't show actions for cost rows
      if ((row.original as ExtendedScorecardDataItem).isCostRow) {
        return null;
      }

      if (isArchivedTable) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <RowActions
              rowData={row.original}
              archived
              disabled={shouldDisableActions}
            />
          </Box>
        );
      }

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <RowActions
            rowData={row.original}
            archived={false}
            disabled={shouldDisableActions}
          />
        </Box>
      );
    },

  } : null;

  const fixedColumns: MRT_ColumnDef<ExtendedScorecardDataItem>[] = [
    ...(actionsColumn ? [actionsColumn] : []),
    labelsColumn,
    {
      accessorKey: "categoryName",
      header: "Category",
      size: 150,
      minSize: 100,
      enablePinning: true,
      enableColumnActions: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const cellValue = row.getValue(columnId);
        const filterStr = String(filterValue).toLowerCase().trim();
        return String(cellValue).toLowerCase().startsWith(filterStr);
      },
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
          fontWeight: 600,
          color: "#054254",
          paddingRight: "16px",
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          "& .MuiTableSortLabel-root": {
            display: "none",
          },
        },
      },
      muiTableBodyCellProps: ({ row }) => ({
        sx: {
          backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
            ? "#e8f5e1"
            : (isArchivedTable ? "#E5EAF3" : "#F8FAFC"),
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          height: "auto",
          minHeight: "48px",
          padding: "12px 16px",
          verticalAlign: "top",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "break-word",
        },
      }),
      Cell: ({ cell }) => (
        <div style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          lineHeight: "1.4",
          margin: 0,
        }}>
          {cell.getValue<string>()}
        </div>
      ),
    },
    {
      accessorKey: "kpiName",
      header: "KPI Name",
      size: 200,
      minSize: 150,
      enablePinning: true,
      enableColumnActions: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const cellValue = row.getValue(columnId);
        const filterStr = String(filterValue).toLowerCase().trim();
        return String(cellValue).toLowerCase().startsWith(filterStr);
      },
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
          fontWeight: 600,
          color: "#054254",
          paddingRight: "16px",
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          "& .MuiTableSortLabel-root": {
            display: "none",
          },
        },
      },
      muiTableBodyCellProps: ({ row }) => ({
        sx: {
          backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
            ? "#e8f5e1"
            : (isArchivedTable ? "#E5EAF3" : "#F8FAFC"),
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          height: "auto",
          minHeight: "48px",
          padding: "12px 16px",
          verticalAlign: "top",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "break-word",
          "& .MuiTableSortLabel-root": {
            display: "none",
          },
        },
      }),
      Cell: ({ cell }) => (
        <div style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          lineHeight: "1.4",
          margin: 0,
        }}>
          {cell.getValue<string>()}
        </div>
      ),
    },
    {
      accessorKey: "kpiType",
      header: "KPI Type",
      size: 150,
      minSize: 100,
      enableColumnActions: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const cellValue = row.getValue(columnId);
        const filterStr = String(filterValue).toLowerCase().trim();
        return String(cellValue ?? "").toLowerCase().startsWith(filterStr);
      },
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
          fontWeight: 600,
          color: "#054254",
          paddingRight: "16px",
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          "& .MuiTableSortLabel-root": {
            display: "none",
          },
        },
      },
      muiTableBodyCellProps: ({ row }) => ({
        sx: {
          backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
            ? "#e8f5e1"
            : (isArchivedTable ? "#E5EAF3" : "#F8FAFC"),
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          height: "auto",
          minHeight: "48px",
          padding: "12px 16px",
          verticalAlign: "top",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "break-word",
        },
      }),
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        const display = value == null || String(value).trim() === "" ? "-" : value;
        return (
          <div style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            lineHeight: "1.4",
            margin: 0,
          }}>
            {display}
          </div>
        );
      },
    },
    {
      accessorKey: "kpiSubType",
      header: "KPI Subtype",
      size: 150,
      minSize: 100,
      enableColumnActions: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const cellValue = row.getValue(columnId);
        const filterStr = String(filterValue).toLowerCase().trim();
        return String(cellValue ?? "").toLowerCase().startsWith(filterStr);
      },
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: isArchivedTable ? "#E5EAF3" : "#F8FAFC",
          fontWeight: 600,
          color: "#054254",
          paddingRight: "16px",
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          "& .MuiTableSortLabel-root": {
            display: "none",
          },
        },
      },
      muiTableBodyCellProps: ({ row }) => ({
        sx: {
          backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
            ? "#e8f5e1"
            : (isArchivedTable ? "#E5EAF3" : "#F8FAFC"),
          borderRight: "1px solid #C0D0D4",
          borderBottom: "1px solid #C0D0D4",
          height: "auto",
          minHeight: "48px",
          padding: "12px 16px",
          verticalAlign: "top",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "break-word",
        },
      }),
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        const display = value == null || String(value).trim() === "" ? "-" : value;
        return (
          <div style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            lineHeight: "1.4",
            margin: 0,
          }}>
            {display}
          </div>
        );
      },
    },
  ];

  // Generate dynamic columns based on period type and date ranges
  const dynamicColumns: MRT_ColumnDef<ExtendedScorecardDataItem>[] = [];

  // Process each date range to generate columns
  dateRanges.forEach((dateRange) => {
    const startDate = dayjs(dateRange.startDate);
    const endDate = dayjs(dateRange.endDate);

    switch (periodType) {
      case "Monthly": {
        let currentDate = startDate.startOf("month");
        const endMonth = endDate.endOf("month");

        while (currentDate.isBefore(endMonth) || currentDate.isSame(endMonth, "month")) {
          const monthKey = `${currentDate.format("MMM")} - ${currentDate.format("YYYY")}`;
          const monthLabel = currentDate.format("MMM 'YY");

          dynamicColumns.push(createDynamicColumn(monthKey, monthLabel, isArchivedTable, currentTimeframe, onSuccess, userRoles, disableAllCells));
          currentDate = currentDate.add(1, "month");
        }
        break;
      }

      case "Quarterly": {
        // Use fiscalYear from dateRange if available, otherwise fallback to calculating from startDate
        const fiscalYear = dateRange.fiscalYear || startDate.year();

        // Generate 4 quarters for the fiscal year
        for (let quarter = 1; quarter <= 4; quarter += 1) {
          const quarterKey = `Q${quarter} - ${fiscalYear || 2025}`; // e.g., "Q1 - 2025"
          const quarterLabel = `Q${quarter} '${(fiscalYear || 2025).toString().slice(-2)}`; // e.g., "Q1 '25"

          dynamicColumns.push(createDynamicColumn(quarterKey, quarterLabel, isArchivedTable, currentTimeframe, onSuccess, userRoles, disableAllCells));
        }
        break;
      }

      case "Yearly": {
        // Use fiscalYear from dateRange if available, otherwise fallback to calculating from startDate
        const fiscalYear = dateRange.fiscalYear || startDate.year();
        const yearKey = (fiscalYear || 2025).toString(); // e.g., "2025"
        const yearLabel = (fiscalYear || 2025).toString(); // e.g., "2025"

        dynamicColumns.push(createDynamicColumn(yearKey, yearLabel, isArchivedTable, currentTimeframe, onSuccess, userRoles, disableAllCells));

        break;
      }

      default:
        throw new Error(`Unsupported period type: ${periodType}`);
    }
  });

  return [...fixedColumns, ...dynamicColumns];
}; 